import { sbEditable } from '@storyblok/storyblok-editable';
import type { StoryblokComponent } from 'storyblok-js-client';
import DynamicComponent from 'components/DynamicComponent';
import Heading from 'components/atoms/heading/Heading';
import PrimaryButton from 'components/atoms/primary-button/PrimaryButton';
import RichText from 'components/blocks/rich-text/RichText';
import type { StoryblokRichtext } from 'storyblok-rich-text-react-renderer';
import { useScrollProviderContext } from 'components/unlisted/ScrollProvider';
import type { ScrollProviderContextType } from 'components/unlisted/ScrollProvider';
import type { RichTextBlokData } from 'components/blocks/rich-text/RichText.data';
import styles from './CampaignPage.module.scss';

export default function CampaignPage({
  blok,
  canTransitionIn,
}: {
  blok: StoryblokComponent<'campaign_page'> & {
    title?: string;
    download_label?: string;
    introduction?: StoryblokRichtext;
    introduction_use_accent_color?: boolean;
    hero_media?: StoryblokComponent<string>[];
    modules?: (StoryblokComponent<string> & { anchor_id?: string })[];
  };
  canTransitionIn?: boolean;
}) {
  const { scrollTo, scrollStatusRef } = useScrollProviderContext() as ScrollProviderContextType;
  const download = blok.modules?.find((item) => item.component === 'campaign_download');
  const jumpToDownload = () => {
    const target = document.getElementById(download?.anchor_id || 'campaign-report-download');
    if (!target) return;
    const offset = scrollStatusRef.current?.offset.y || 0;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scrollTo(0, offset + target.getBoundingClientRect().top, reduced ? 0 : 600);
    target.focus({ preventScroll: true });
  };
  return (
    <main id="main-content" {...sbEditable(blok)} className={styles.page}>
      <div className={styles.hero}>
        <Heading
          as="h1"
          size="heading-03"
          className={styles.title}
          transitionInType="none"
          disableTriggerInOnScroll
        >
          {blok.title}
        </Heading>
        {download && (
          <PrimaryButton
            label={blok.download_label || 'Download report'}
            onClick={jumpToDownload}
            noTransition
            disableTriggerInOnScroll
          />
        )}
        {blok.hero_media?.map((item) => (
          <DynamicComponent key={item._uid} blok={item} canTransitionIn={canTransitionIn} />
        ))}
        {blok.introduction?.content && (
          <RichText
            blok={{
              _uid: `${blok._uid}-introduction`,
              component: 'rich_text',
              title: '',
              text: blok.introduction,
              use_accent_color: !!blok.introduction_use_accent_color,
            }}
            className={styles.introduction}
            canTransitionIn={canTransitionIn}
          />
        )}
      </div>
      {blok.modules?.map((item) =>
        item.component === 'rich_text' ? (
          <RichText
            key={item._uid}
            blok={item as typeof item & RichTextBlokData}
            className={styles.textSection}
            canTransitionIn={canTransitionIn}
          />
        ) : (
          <DynamicComponent key={item._uid} blok={item} canTransitionIn={canTransitionIn} />
        ),
      )}
    </main>
  );
}
